import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  MouseSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnPinningState,
  type ColumnSizingState,
  type ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { TableStateControl } from '../../hooks';
import { cn } from '../../styles/lib/utils';
import { DEFAULT_PAGE_SIZE } from '../../utils/tableSettings';
import { Checkbox } from '../Checkbox';
import type { ColumnMoveDirection, ColumnResizeAction } from '../ColumnMenu';
import { closestLeftEdgeCollisionDetectionWithHideColumnDropzone, DragOverlay } from '../Drag';
import { Grid, type GridSpacing } from '../Grid';
import { GridHeadDragOverlay } from '../GridHead/GridHead';
import { SearchButtonInput } from '../SearchButtonInput';
import { TablePagination } from '../TablePagination';
import { MemoizedTableBody, TableBody } from './TableBody';
import { TableHeader } from './TableHeader';
import { useGridNavigation } from './useGridNavigation';
import { flattenColumns, getPinnedCellLayouts, readPinnedBands, toCssVarName } from './utils';

export type TableColumn<TData> = ColumnDef<TData> & { id: string };

// Pixels per Narrower / Wider press in the column menu.
const COLUMN_RESIZE_STEP = 25;

const PINNED_SECTION_NAMES = { left: 'left pinned columns', right: 'right pinned columns' } as const;

// Wide enough for the label, the header ⓘ, the column menu button and the resize handle.
const HEADER_TOOLTIP_MIN_SIZE = 110;

function withHeaderTooltipMinSize<TData>(defs: ColumnDef<TData, unknown>[]): ColumnDef<TData, unknown>[] {
  return defs.map((def) => {
    if ('columns' in def && def.columns) {
      return { ...def, columns: withHeaderTooltipMinSize(def.columns) };
    }
    const hasTooltip = Boolean((def.meta as { headerTooltip?: unknown } | undefined)?.headerTooltip);
    return hasTooltip && def.minSize === undefined ? { ...def, minSize: HEADER_TOOLTIP_MIN_SIZE } : def;
  });
}

/**
 * Dropzone component for hiding columns
 * When a column is dragged and dropped here, it will be hidden
 */
function HideColumnDropzone() {
  const { setNodeRef } = useDroppable({
    id: 'hide-column-dropzone',
  });

  return <div ref={setNodeRef} className={cn('flex h-0 w-full')} />;
}

export type TableProps<TData> = {
  data: TData[];
  // Make "id" required on every column definition for runtime and static type safety.
  columns: TableColumn<TData>[];
  spacing?: GridSpacing;
  enableRowSelection?: boolean;
  enableColumnGrouping?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: (sizing: ColumnSizingState) => void;
  columnPinning?: ColumnPinningState;
  onColumnPinningChange?: (pinning: ColumnPinningState) => void;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: (order: ColumnOrderState) => void;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  globalFilter?: string;
  onGlobalFilterChange?: (filter: string) => void;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  enableColumnResizing?: boolean;
  enableColumnPinning?: boolean;
  enableHiding?: boolean;
  enableSorting?: boolean;
  enableColumnFilters?: boolean;
  enableColumnOrdering?: boolean;
  manualSorting?: boolean;
  manualFiltering?: boolean;
  manualPagination?: boolean;
  fixedPinning?: boolean;
  pageCount?: number;
  className?: string;
  /**
   * Optional table state control (from useTableUrlState or useTableStoreState).
   * If provided, will override individual sorting/pagination props.
   */
  stateControl?: TableStateControl;
  isLoading?: boolean;
  isFetching?: boolean;
  /**
   * Stable row id for each row. Required for server-side pagination with row selection
   * so selection state survives page changes (use e.g. row.id from your API).
   */
  getRowId?: (row: TData) => string;
  /** Names the row checkbox "Select <label>"; must be unique per row so each checkbox names its own row. */
  getRowLabel?: (row: TData) => string;
  /**
   * When using manualPagination + enableRowSelection, pass this to support "select all across all pages".
   * Header checkbox and row selection will use these callbacks instead of page-only table state.
   */
  serverSideSelection?: {
    isAllSelected: boolean;
    isSomeSelected: boolean;
    getIsRowSelected: (rowId: string) => boolean;
    onSelectAllChange: (selected: boolean) => void;
    onRowToggle: (rowId: string, selected: boolean) => void;
  };
  /** When true, clicking anywhere on a row toggles its selection. Defaults to false (checkbox only). */
  enableRowClickSelection?: boolean;
  /**
   * Called when a row is clicked, after any click-to-select toggle. A mouse shortcut only: whatever it does must
   * also be reachable from a focusable control in the row or page. Like click-to-select, every click in the row
   * counts; a control that shouldn't trigger it calls `event.stopPropagation()`.
   */
  onRowClick?: (row: Row<TData>) => void;
  /**
   * Row ids to render with the highlighted (blue) row background, independent of selection.
   * Use for transient emphasis such as a row whose detail popover/menu is open.
   */
  highlightedRowIds?: ReadonlySet<string>;
  /**
   * Column that grows into spare width and shrinks to its unwrapped content width so the table fits without
   * horizontal scroll; below that width the table scrolls horizontally.
   */
  expandColumnId?: string;
  /**
   * Whether the hovered row tints grey (the row-hover highlight). Defaults to true.
   * Independent of the hover TRACKING: the hovered row is always stamped with
   * `data-hovered` (mouse or keyboard focus, synced across pinned sections), so cell
   * content revealed via `group-data-[hovered=true]/row:*` keeps working with the
   * highlight off.
   */
  enableRowHoverHighlight?: boolean;
  /**
   * Accordion rows: when true, rows returned by getSubRows render as expandable
   * children of their parent, in the same column grid and stripe. Expansion is
   * client-side render state only — it resets whenever the data array changes
   * (e.g. a page/sort/filter refetch) and never participates in sorting, filtering,
   * or pagination, which see parent rows only.
   */
  enableExpanding?: boolean;
  /** Returns a row's child rows. Required for enableExpanding to have any effect. */
  getSubRows?: (row: TData) => TData[] | undefined;
  /**
   * Maximum height of the table. If not provided, the table will be as tall as its content.
   */
  maxHeight?: number;
  /**
   * When true (with `enableExpanding`), each currently-expanded row with children sticks to the
   * top of the table, below the column header, stacking below any other expanded rows above it
   * in scroll order. Multiple rows can be stacked at once — there is no single-header replacement.
   */
  stickyExpandedRows?: boolean;
  /**
   * Renders a "start-end of total" range + prev/next controls above the table rows. Off
   * by default so no existing consumer's layout changes; opt in for a plain `Table` with
   * no other pagination UI of its own (e.g. no `ServerTable` toolbar composed around it).
   *
   * Combined with `manualPagination`, pass `rowCount` for the true total, and keep
   * `pageIndex` in range yourself — TanStack only auto-resets it for client-side pagination.
   */
  showPagination?: boolean;
  /**
   * Renders a search input above the table rows, alongside pagination when both are on
   * (search left, pagination right). Off by default. Bound to the same global filter as
   * `globalFilter`/`stateControl`; restrict which columns match with `enableGlobalFilter`
   * on the column def plus a `getColumnCanGlobalFilter` table option.
   */
  showSearch?: boolean;
  /**
   * Keyboard model. `'grid'` makes the table one Tab stop with arrow-key navigation between cells (WAI-ARIA data
   * grid); `'table'` keeps a plain table where Tab visits every control. `'auto'` (default) picks the grid when the
   * table has row selection, a sortable column, column reordering or a column menu (resizable columns have one).
   * Pass `'grid'` for editable cells.
   */
  keyboardNavigation?: 'auto' | 'grid' | 'table';
  /** Names the table for screen readers; prefer `aria-labelledby` when a visible heading sits above it. */
  'aria-label'?: string;
  /** Id of the visible heading that names the table for screen readers. */
  'aria-labelledby'?: string;
} & Omit<TableOptions<TData>, 'data' | 'columns' | 'getCoreRowModel'>;

// Engines without :focus-visible throw on the selector; treat their focus as visible.
function isFocusVisible(el: Element): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
}

function Table<TData>({
  data,
  columns,
  spacing = 'cozy',
  enableRowSelection = false,
  enableColumnGrouping = false,
  rowSelection,
  onRowSelectionChange,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  columnSizing,
  onColumnSizingChange,
  columnPinning,
  onColumnPinningChange,
  columnOrder,
  onColumnOrderChange,
  columnVisibility,
  onColumnVisibilityChange,
  globalFilter,
  onGlobalFilterChange,
  pagination,
  onPaginationChange,
  rowCount,
  enableColumnResizing = true,
  enableColumnPinning = true,
  enableHiding = true,
  enableSorting = true,
  enableColumnFilters = true,
  enableColumnOrdering = true,
  manualSorting = false,
  manualFiltering = false,
  manualPagination = false,
  fixedPinning = false, // Make true for tables with fixed pinned columns + disabled custom pinning
  pageCount,
  className,
  stateControl,
  isLoading = false,
  isFetching = false,
  getRowId,
  getRowLabel,
  serverSideSelection,
  enableRowClickSelection = false,
  onRowClick,
  highlightedRowIds,
  expandColumnId,
  enableRowHoverHighlight = true,
  enableExpanding = false,
  getSubRows,
  maxHeight,
  stickyExpandedRows = false,
  showPagination = false,
  showSearch = false,
  keyboardNavigation = 'auto',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ...tableOptions
}: TableProps<TData>) {
  // Server state if provided, otherwise use internal state
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [internalGlobalFilter, setInternalGlobalFilter] = useState<string>('');
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  // Local column filters state
  const [internalColumnFilters, setInternalColumnFilters] = useState<ColumnFiltersState>([]);

  // Local state / settings
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});
  const [internalColumnSizing, setInternalColumnSizing] = useState<ColumnSizingState>({});
  const [internalColumnPinning, setInternalColumnPinning] = useState<ColumnPinningState>(() => ({
    left: enableRowSelection ? ['select'] : [],
    right: [],
  }));
  const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>({});

  // Expansion is a display concern: it isn't lifted to stateControl and auto-resets when the data array changes.
  const [expanded, setExpanded] = useState<ExpandedState>(() => tableOptions.initialState?.expanded ?? {});

  // Initialize column order with all column IDs (including 'select' if row selection is enabled)
  const [internalColumnOrder, setInternalColumnOrder] = useState<ColumnOrderState>(() => {
    // Leaf ids, not top-level ids: TanStack's columnOrder only orders leaf columns,
    // and unlisted leaves get appended after the listed ones, scrambling grouped tables.
    const columnIds = flattenColumns(columns as any[]).map((col) => col.id as string);
    return enableRowSelection ? ['select', ...columnIds] : columnIds;
  });

  // Use stateControl if provided, otherwise use individual props or internal state
  const effectiveSorting = stateControl?.sorting ?? sorting ?? internalSorting;
  const effectivePagination = stateControl?.pagination ?? pagination ?? internalPagination;
  const effectiveGlobalFilter = stateControl?.globalFilter ?? globalFilter ?? internalGlobalFilter;
  const effectiveColumnFilters = columnFilters ?? internalColumnFilters;

  // Local state / settings
  const effectiveRowSelection = rowSelection ?? internalRowSelection;
  const effectiveColumnSizing = columnSizing ?? internalColumnSizing;
  const effectiveColumnOrder = columnOrder ?? internalColumnOrder;
  const effectiveColumnVisibility = columnVisibility ?? internalColumnVisibility;

  // Use stateControl setters if provided
  const effectiveSetSorting = stateControl?.setSorting ?? onSortingChange ?? setInternalSorting;
  const effectiveSetPagination = stateControl?.setPagination ?? onPaginationChange ?? setInternalPagination;
  const effectiveSetGlobalFilter = stateControl?.setGlobalFilter ?? onGlobalFilterChange ?? setInternalGlobalFilter;

  // Ensure 'select' column is always pinned left when row selection is enabled
  const effectiveColumnPinning = useMemo(() => {
    const basePinning = columnPinning ?? internalColumnPinning;
    if (enableRowSelection && !basePinning.left?.includes('select')) {
      return {
        ...basePinning,
        left: ['select', ...(basePinning.left || [])],
      };
    }
    return basePinning;
  }, [columnPinning, internalColumnPinning, enableRowSelection]);

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updater === 'function' ? updater(effectiveSorting) : updater;
      effectiveSetSorting(newSorting);
    },
    [effectiveSorting, effectiveSetSorting],
  );

  const handleColumnFiltersChange = useCallback(
    (updater: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)) => {
      const newFilters = typeof updater === 'function' ? updater(effectiveColumnFilters) : updater;
      if (onColumnFiltersChange) {
        onColumnFiltersChange(newFilters);
      } else {
        setInternalColumnFilters(newFilters);
      }
    },
    [effectiveColumnFilters, onColumnFiltersChange],
  );

  const handleRowSelectionChange = useCallback(
    (updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
      const newSelection = typeof updater === 'function' ? updater(effectiveRowSelection) : updater;
      if (onRowSelectionChange) {
        onRowSelectionChange(newSelection);
      } else {
        setInternalRowSelection(newSelection);
      }
    },
    [effectiveRowSelection, onRowSelectionChange],
  );

  const handleColumnSizingChange = useCallback(
    (updater: ColumnSizingState | ((old: ColumnSizingState) => ColumnSizingState)) => {
      const newSizing = typeof updater === 'function' ? updater(effectiveColumnSizing) : updater;
      if (onColumnSizingChange) {
        onColumnSizingChange(newSizing);
      } else {
        setInternalColumnSizing(newSizing);
      }
    },
    [effectiveColumnSizing, onColumnSizingChange],
  );

  const handleColumnPinningChange = useCallback(
    (updater: ColumnPinningState | ((old: ColumnPinningState) => ColumnPinningState)) => {
      const newPinning = typeof updater === 'function' ? updater(effectiveColumnPinning) : updater;
      if (onColumnPinningChange) {
        onColumnPinningChange(newPinning);
      } else {
        setInternalColumnPinning(newPinning);
      }
    },
    [effectiveColumnPinning, onColumnPinningChange],
  );

  const handleColumnOrderChange = useCallback(
    (updater: ColumnOrderState | ((old: ColumnOrderState) => ColumnOrderState)) => {
      const newOrder = typeof updater === 'function' ? updater(effectiveColumnOrder) : updater;
      if (onColumnOrderChange) {
        onColumnOrderChange(newOrder);
      } else {
        setInternalColumnOrder(newOrder);
      }
    },
    [effectiveColumnOrder, onColumnOrderChange],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const handleColumnVisibilityChange = useCallback(
    (updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => {
      const newVisibility = typeof updater === 'function' ? updater(effectiveColumnVisibility) : updater;

      // Remove pinned columns from pinning state if they're being hidden
      handleColumnPinningChange((prev) => {
        const newPinning = { ...prev };
        let changed = false;

        // Check left pinned columns
        if (newPinning.left) {
          const hiddenLeft = newPinning.left.filter((id) => newVisibility[id] === false);
          if (hiddenLeft.length > 0) {
            newPinning.left = newPinning.left.filter((id) => newVisibility[id] !== false);
            changed = true;
          }
        }

        // Check right pinned columns
        if (newPinning.right) {
          const hiddenRight = newPinning.right.filter((id) => newVisibility[id] === false);
          if (hiddenRight.length > 0) {
            newPinning.right = newPinning.right.filter((id) => newVisibility[id] !== false);
            changed = true;
          }
        }

        return changed ? newPinning : prev;
      });

      if (onColumnVisibilityChange) {
        onColumnVisibilityChange(newVisibility);
      } else {
        setInternalColumnVisibility(newVisibility);
      }
    },
    [effectiveColumnVisibility, onColumnVisibilityChange, handleColumnPinningChange, enableRowSelection],
  );

  const handleGlobalFilterChange = useCallback(
    (updater: string | ((old: string) => string)) => {
      const newFilter = typeof updater === 'function' ? updater(effectiveGlobalFilter) : updater;
      effectiveSetGlobalFilter(newFilter);
    },
    [effectiveGlobalFilter, effectiveSetGlobalFilter],
  );

  const handlePaginationChange = useCallback(
    (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
      const newPagination = typeof updater === 'function' ? updater(effectivePagination) : updater;
      effectiveSetPagination(newPagination);
    },
    [effectivePagination, effectiveSetPagination],
  );

  // Add selection column if enabled
  const tableColumns = useMemo<TableColumn<TData>[]>(() => {
    const sizedColumns = withHeaderTooltipMinSize(columns) as TableColumn<TData>[];
    if (!enableRowSelection) {
      return sizedColumns;
    }

    const useServerSideSelection = manualPagination && serverSideSelection;

    const selectionColumn: TableColumn<TData> = {
      id: 'select',
      header: ({ table }) => {
        const isAllSelected = useServerSideSelection
          ? serverSideSelection.isAllSelected
          : table.getIsAllPageRowsSelected();
        const isSomeSelected = useServerSideSelection
          ? serverSideSelection.isSomeSelected
          : table.getIsSomePageRowsSelected();
        return (
          <Checkbox
            checked={isAllSelected ? true : isSomeSelected ? 'indeterminate' : false}
            onCheckedChange={(checked) => {
              if (useServerSideSelection) {
                serverSideSelection.onSelectAllChange(!!checked);
              } else {
                table.toggleAllPageRowsSelected(!!checked);
              }
            }}
            aria-label="Select all"
            data-testid="table-select-all"
          />
        );
      },
      cell: ({ row, table }) => {
        // No checkbox on accordion child rows when sub-row selection is off.
        if (row.depth > 0 && table.options.enableSubRowSelection === false) return null;
        const rowId = row.id;
        const isSelected = useServerSideSelection ? serverSideSelection.getIsRowSelected(rowId) : row.getIsSelected();
        const handleChange = (checked: boolean) => {
          if (useServerSideSelection) {
            serverSideSelection.onRowToggle(rowId, checked);
          } else {
            row.toggleSelected(checked);
          }
        };
        const label = getRowLabel?.(row.original);
        return (
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => handleChange(!!checked)}
            aria-label={label ? `Select ${label}` : 'Select row'}
            data-testid={`table-select-row-${rowId}`}
          />
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 32,
      minSize: 32,
      maxSize: 32,
    };

    const selectionColumnGroup = {
      id: 'select',
      header: '',
      columns: [selectionColumn],
    };

    if (enableColumnGrouping) {
      return [selectionColumnGroup, ...sizedColumns];
    }

    return [selectionColumn, ...sizedColumns];
  }, [columns, enableRowSelection, enableColumnGrouping, manualPagination, serverSideSelection, getRowLabel]);

  const table = useReactTable<TData>({
    data,
    columns: tableColumns,
    ...(getRowId ? { getRowId } : {}),
    getCoreRowModel: getCoreRowModel(),
    ...(enableExpanding ? { getSubRows, getExpandedRowModel: getExpandedRowModel() } : {}),
    ...(manualFiltering ? { manualFiltering: true } : { getFilteredRowModel: getFilteredRowModel() }),
    ...(manualSorting ? { manualSorting: true } : { getSortedRowModel: getSortedRowModel() }),
    ...(manualPagination
      ? { manualPagination: true, rowCount: rowCount ?? data?.length }
      : { getPaginationRowModel: getPaginationRowModel() }),
    state: {
      sorting: effectiveSorting,
      columnFilters: effectiveColumnFilters,
      rowSelection: effectiveRowSelection,
      columnSizing: effectiveColumnSizing,
      columnPinning: effectiveColumnPinning,
      columnOrder: effectiveColumnOrder,
      columnVisibility: effectiveColumnVisibility,
      globalFilter: effectiveGlobalFilter,
      pagination: effectivePagination,
      expanded,
    },
    onExpandedChange: setExpanded,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnSizingChange: handleColumnSizingChange,
    onColumnPinningChange: handleColumnPinningChange,
    onColumnOrderChange: enableColumnOrdering ? handleColumnOrderChange : undefined,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onGlobalFilterChange: handleGlobalFilterChange,
    onPaginationChange: handlePaginationChange,
    columnResizeMode: 'onChange',
    pageCount,
    enableColumnResizing,
    enableRowSelection,
    enableColumnPinning,
    enableHiding,
    enableSorting,
    enableColumnFilters,
    enableExpanding,
    ...tableOptions,
    // After the spread, so a caller's partial defaultColumn adds to these limits instead of replacing them.
    defaultColumn: {
      minSize: 85,
      maxSize: 800,
      ...tableOptions.defaultColumn,
    },
  });

  /**
   * Instead of calling `column.getSize()` on every render for every header
   * and especially every data cell (very expensive),
   * we will calculate all column sizes at once at the root table level in a useMemo
   * and pass the column sizes down as CSS variables to the Grid element.
   */
  // Toggles a row's selection from a row click or Space; skips sub-rows that show no checkbox.
  const handleToggleRowSelection = useCallback(
    (rowId: string) => {
      const row = table.getRowModel().rowsById[rowId];
      if (!row || (row.depth > 0 && table.options.enableSubRowSelection === false)) return;
      if (manualPagination && serverSideSelection) {
        serverSideSelection.onRowToggle(rowId, !serverSideSelection.getIsRowSelected(rowId));
      } else {
        row.toggleSelected();
      }
    },
    [table, manualPagination, serverSideSelection],
  );

  // Row clicks toggle selection when click-to-select is on, then call `onRowClick`.
  const rowClickHandler = useCallback(
    (select: boolean) => {
      if (!select && !onRowClick) return undefined;
      return (rowIndex: number) => {
        const row = table.getRowModel().rows[rowIndex];
        if (!row) return;
        if (select) handleToggleRowSelection(row.id);
        if (onRowClick) onRowClick(row);
      };
    },
    [handleToggleRowSelection, onRowClick, table],
  );
  // Clicks on the right-pinned columns toggle click-to-select even when the checkbox column is off.
  const handleRowClick = useMemo(() => {
    const handleCenterClick = rowClickHandler(enableRowClickSelection && enableRowSelection);
    const handleRightClick = rowClickHandler(enableRowClickSelection);
    if (!handleCenterClick && !handleRightClick) return undefined;
    return (rowIndex: number, event: ReactMouseEvent<HTMLTableRowElement>) => {
      const inRight = event.target instanceof Element && event.target.closest('[data-pinned="right"]');
      (inRight ? handleRightClick : handleCenterClick)?.(rowIndex);
    };
  }, [rowClickHandler, enableRowClickSelection, enableRowSelection]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i];
      colSizes[`--header-${toCssVarName(header.id)}-size`] = header.getSize();
      colSizes[`--col-${toCssVarName(header.column.id)}-size`] = header.column.getSize();
    }
    return colSizes;
  }, [table.getState().columnSizingInfo, table.getState().columnSizing, effectiveColumnOrder, effectiveColumnPinning]);

  // Setup DnD sensors
  const sensors = useSensors(
    // Mouse sensor with a distance constraint of 5 pixels
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5, // Drag starts after moving 5 pixels
      },
    }),
    // Touch sensor with a delay and tolerance constraint
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // User must hold for 200ms
        tolerance: 5, // Allows for 5 pixels of movement during the delay
      },
    }),
  );

  // Get visual column groups (left pinned, center, right pinned)
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const columnGroups = useMemo(() => {
    const leftColumns = table.getLeftLeafColumns().map((col) => col.id);
    const centerColumns = table.getCenterLeafColumns().map((col) => col.id);
    const rightColumns = table.getRightLeafColumns().map((col) => col.id);

    return {
      left: leftColumns,
      center: centerColumns,
      right: rightColumns,
      all: [...leftColumns, ...centerColumns, ...rightColumns],
    };
  }, [table, effectiveColumnPinning, effectiveColumnOrder]);

  // Helper function to determine which section a column belongs to
  const getSection = useCallback(
    (id: string): 'left' | 'center' | 'right' | null => {
      // Handle dropzone IDs
      if (id.endsWith('-end-dropzone')) {
        const section = id.replace('-end-dropzone', '') as 'left' | 'center' | 'right';
        if (section === 'left' || section === 'center' || section === 'right') {
          return section;
        }
      }
      if (columnGroups.left.includes(id)) return 'left';
      if (columnGroups.right.includes(id)) return 'right';
      if (columnGroups.center.includes(id)) return 'center';
      return null;
    },
    [columnGroups],
  );

  // Shared logic for reordering columns within and between sections
  const reorderColumns = useCallback(
    (activeId: string, overId: string) => {
      // Don't allow dragging the select column
      if (activeId === 'select') return;

      const activeSection = getSection(activeId);
      const overSection = getSection(overId);

      if (!activeSection || !overSection) return;
      // Moving a header into another section pins or unpins it.
      if (!enableColumnPinning && activeSection !== overSection) return;

      // Check if dropping on an end dropzone
      const isEndDropzone = overId.endsWith('-end-dropzone');

      // Get the target section array (where we're dropping)
      const targetSectionArray =
        overSection === 'left' ? columnGroups.left : overSection === 'right' ? columnGroups.right : columnGroups.center;

      // Filter out 'select' from the target section array
      const targetSectionWithoutSelect = targetSectionArray.filter((id) => id !== 'select');

      // Get the source section array (where we're dragging from)
      const sourceSectionArray =
        activeSection === 'left'
          ? columnGroups.left
          : activeSection === 'right'
            ? columnGroups.right
            : columnGroups.center;

      // Remove active column from source section
      const sourceWithoutActive = sourceSectionArray.filter((id) => id !== activeId && id !== 'select');

      // Insert active column into target section at the correct position
      const targetWithoutSelect = targetSectionWithoutSelect.filter((id) => id !== activeId);
      let insertIndex: number;

      if (isEndDropzone) {
        // Insert at the end
        insertIndex = targetWithoutSelect.length;
      } else {
        // Insert before the overId item
        insertIndex = targetWithoutSelect.indexOf(overId);
        if (insertIndex === -1) return;
      }

      const reorderedTarget = [
        ...targetWithoutSelect.slice(0, insertIndex),
        activeId,
        ...targetWithoutSelect.slice(insertIndex),
      ];

      // If moving between sections, update pin status and remove from source section
      if (activeSection !== overSection) {
        // Get the column from the table to update its pin status
        const column = table.getColumn(activeId);
        if (!column) return;

        // Update pinning state to reflect the move
        handleColumnPinningChange((prev) => {
          const newPinning = { ...prev };

          // Remove from source section
          if (activeSection === 'left') {
            newPinning.left = sourceWithoutActive.filter((id) => id !== 'select');
            if (enableRowSelection && newPinning.left.length > 0 && !newPinning.left.includes('select')) {
              // Keep select first if it exists
              const hasSelect = columnGroups.left.includes('select');
              newPinning.left = hasSelect ? ['select', ...newPinning.left] : newPinning.left;
            }
          } else if (activeSection === 'right') {
            newPinning.right = sourceWithoutActive;
          }

          // Add to target section
          if (overSection === 'left') {
            const hasSelect = enableRowSelection && columnGroups.left.includes('select');
            newPinning.left = hasSelect ? ['select', ...reorderedTarget] : reorderedTarget;
          } else if (overSection === 'right') {
            newPinning.right = reorderedTarget;
          }

          return newPinning;
        });

        // If moving to or from center, also update columnOrder
        if (overSection === 'center' || activeSection === 'center') {
          const newLeft =
            overSection === 'left'
              ? reorderedTarget
              : columnGroups.left.filter((id) => id !== activeId && id !== 'select');
          const newCenter =
            overSection === 'center' ? reorderedTarget : columnGroups.center.filter((id) => id !== activeId);
          const newRight =
            overSection === 'right' ? reorderedTarget : columnGroups.right.filter((id) => id !== activeId);
          const hasSelect =
            enableRowSelection &&
            newLeft.length > 0 &&
            !newLeft.includes('select') &&
            columnGroups.left.includes('select');
          const finalLeft = hasSelect ? ['select', ...newLeft] : newLeft;
          handleColumnOrderChange([...finalLeft, ...newCenter, ...newRight]);
        }
      } else {
        // Same section reordering
        if (overSection === 'left') {
          // Update left pinned columns
          handleColumnPinningChange((prev) => {
            const newPinning = { ...prev };
            const hasSelect = enableRowSelection && columnGroups.left.includes('select');
            newPinning.left = hasSelect ? ['select', ...reorderedTarget] : reorderedTarget;
            return newPinning;
          });
        } else if (overSection === 'right') {
          // Update right pinned columns
          handleColumnPinningChange((prev) => {
            const newPinning = { ...prev };
            newPinning.right = reorderedTarget;
            return newPinning;
          });
        } else {
          // Update center (unpinned) columns
          // Reconstruct the full column order with center section updated
          const newVisualOrder = [...columnGroups.left, ...reorderedTarget, ...columnGroups.right];
          handleColumnOrderChange(newVisualOrder);
        }
      }
    },
    [
      columnGroups,
      handleColumnOrderChange,
      handleColumnPinningChange,
      enableRowSelection,
      enableColumnPinning,
      getSection,
      table,
    ],
  );

  const [columnStatus, setColumnStatus] = useState('');

  const getColumnLabel = useCallback(
    (columnId: string) => {
      const header = table.getColumn(columnId)?.columnDef.header;
      return typeof header === 'string' ? header : columnId;
    },
    [table],
  );

  const moveColumn = useCallback(
    (columnId: string, direction: ColumnMoveDirection) => {
      const section = getSection(columnId);
      if (!section) return;
      const ids = columnGroups[section].filter((id) => id !== 'select' && table.getColumn(id)?.getIsVisible());
      const index = ids.indexOf(columnId);
      const newIndex = direction === 'left' ? index - 1 : index + 1;
      if (index === -1 || newIndex < 0 || newIndex >= ids.length) return;

      // Right swaps the next column in front, since reorderColumns inserts before its target.
      if (direction === 'left') reorderColumns(columnId, ids[newIndex]);
      else reorderColumns(ids[newIndex], columnId);

      const sectionName = section === 'center' ? '' : ` in ${PINNED_SECTION_NAMES[section]}`;
      setColumnStatus(`${getColumnLabel(columnId)} moved to position ${newIndex + 1} of ${ids.length}${sectionName}`);
    },
    [columnGroups, getColumnLabel, getSection, reorderColumns, table],
  );

  const resizeColumn = useCallback(
    (columnId: string, action: ColumnResizeAction) => {
      const column = table.getColumn(columnId);
      if (!column) return;
      const { size = column.getSize(), minSize = 0, maxSize = Number.MAX_SAFE_INTEGER } = column.columnDef;
      const clamp = (width: number) => Math.min(Math.max(width, minSize), maxSize);

      let width: number;
      if (action === 'reset') {
        width = clamp(size);
        column.resetSize();
      } else {
        width = clamp(column.getSize() + (action === 'wider' ? COLUMN_RESIZE_STEP : -COLUMN_RESIZE_STEP));
        table.setColumnSizing((old) => ({ ...old, [columnId]: width }));
      }
      setColumnStatus(`${getColumnLabel(columnId)} width ${width} pixels`);
    },
    [getColumnLabel, table],
  );

  // Handle drag over to track drop position (no reordering during drag)
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!active || !over) {
      setOverId(null);
      return;
    }

    const overId = over.id as string;
    setOverId(overId);
  }, []);

  // Track active drag ID for DragOverlay
  const [activeId, setActiveId] = useState<string | null>(null);
  // Track over ID to show drop indicator
  const [overId, setOverId] = useState<string | null>(null);

  // Track if we're in a performance-critical operation (resize or drag)
  const isResizingColumn = table.getState().columnSizingInfo.isResizingColumn;
  const isDraggingColumn = activeId !== null;

  // Handle drag end to reorder columns within the same section only
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!active || !over || active.id === over.id) return;

      const activeId = active.id as string;
      const overId = over.id as string;

      // Check if dropped on hide column dropzone
      if (overId === 'hide-column-dropzone') {
        // Hide the column
        const column = table.getColumn(activeId);
        if (column?.getCanHide()) {
          column.toggleVisibility(false);
          // If hiding a pinned column, unpin it
          if (column.getIsPinned()) {
            column.pin(false);
          }
        }
        return;
      }

      reorderColumns(activeId, overId);
    },
    [reorderColumns, table],
  );

  // Handle drag start to set activeId
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const activeId = event.active.id as string;
    setActiveId(activeId);
    setOverId(null);
  }, []);

  // Handle drag end to clear state
  const handleDragEndWithCleanup = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      setOverId(null);
      handleDragEnd(event);
    },
    [handleDragEnd],
  );

  // Get the active header and cells for DragOverlay
  const activeHeader = useMemo(() => {
    if (!activeId) return null;
    const headers = table.getFlatHeaders();
    return headers.find((h) => h.column.id === activeId);
  }, [activeId, table]);

  const sectionsContainerRef = useRef<HTMLDivElement>(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: recomputed when pinning, order or visibility changes
  const pinnedLayouts = useMemo(
    () =>
      getPinnedCellLayouts(
        table.getLeftVisibleLeafColumns().map((col) => col.id),
        table.getRightVisibleLeafColumns().map((col) => col.id),
      ),
    [table, effectiveColumnPinning, effectiveColumnOrder, effectiveColumnVisibility],
  );

  // Measured so sticky expanded rows (see TableBody) offset below the header at any spacing or column grouping.
  useLayoutEffect(() => {
    if (!stickyExpandedRows) return;
    const header = sectionsContainerRef.current?.querySelector<HTMLElement>('[data-slot="grid-header"]');
    if (!header) return;

    const measure = () => {
      const height = header.getBoundingClientRect().height;
      setStickyHeaderHeight((prev) => (prev === height ? prev : height));
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, [stickyExpandedRows]);

  // Browser focus scrolling ignores the sticky header, stuck rows and pinned columns, so a focused control can hide.
  // Pointer focus is skipped so a click on a partly hidden control doesn't scroll the table.
  const revealFocusedControl = useCallback((event: FocusEvent) => {
    const container = sectionsContainerRef.current;
    const target = event.target;
    if (!container || !(target instanceof HTMLElement)) return;
    if (target.closest('[data-slot="table-sections"]') !== container) return;
    if (!isFocusVisible(target)) return;
    const rect = target.getBoundingClientRect();

    const headerBottom = container.querySelector('[data-slot="grid-header"]')?.getBoundingClientRect().bottom;
    if (headerBottom !== undefined && !target.closest('[data-slot="grid-header"]')) {
      // A sticky row is stuck, not in flow, when it sits at its offset below the header.
      const stuckRect = (row: HTMLElement) => {
        const rowRect = row.getBoundingClientRect();
        const stuck = Math.abs(rowRect.top - (headerBottom + Number(row.dataset.stickyRow))) <= 1;
        return stuck ? rowRect : undefined;
      };
      const targetRow = target.closest<HTMLElement>('[data-sticky-row]');
      if (!targetRow || !stuckRect(targetRow)) {
        let bandBottom = headerBottom;
        for (const row of container.querySelectorAll<HTMLElement>('[data-sticky-row]')) {
          if (row.closest('[data-slot="table-sections"]') !== container) continue;
          const rowRect = stuckRect(row);
          if (rowRect && rowRect.left < rect.right && rowRect.right > rect.left) {
            bandBottom = Math.max(bandBottom, rowRect.bottom);
          }
        }
        if (rect.top < bandBottom) container.scrollTop -= bandBottom - rect.top;
      }
    }

    if (target.closest('[data-pinned]')) return;
    const bands = readPinnedBands(container);
    const leftEdge = bands.left?.right;
    const rightEdge = bands.right?.left;
    if (leftEdge !== undefined && rect.left < leftEdge) container.scrollLeft -= leftEdge - rect.left;
    else if (rightEdge !== undefined && rect.right > rightEdge) container.scrollLeft += rect.right - rightEdge;
  }, []);

  useEffect(() => {
    const container = sectionsContainerRef.current;
    if (!container) return;
    container.addEventListener('focusin', revealFocusedControl);
    return () => container.removeEventListener('focusin', revealFocusedControl);
  }, [revealFocusedControl]);

  const isGrid =
    keyboardNavigation === 'grid' ||
    (keyboardNavigation === 'auto' &&
      (enableRowSelection ||
        !!table.options.onColumnOrderChange ||
        table
          .getVisibleLeafColumns()
          .some(
            (column) =>
              column.id !== 'select' &&
              (column.getCanSort() ||
                column.getCanHide() ||
                column.getCanFilter() ||
                column.getCanResize() ||
                (column.getCanPin() && !fixedPinning)),
          )));

  const gridRef = useRef<HTMLTableElement>(null);
  const gridInstructionsId = useId();
  const gridNavigation = useGridNavigation({
    enabled: isGrid,
    gridRef,
    scrollContainerRef: sectionsContainerRef,
    instructionsId: gridInstructionsId,
    onToggleRowSelection: enableRowSelection ? handleToggleRowSelection : undefined,
    paused: !!isResizingColumn || isDraggingColumn,
  });

  // A page holding only some rows tells assistive technology where it sits in the whole set; expanded sub-rows have no known total.
  const headerRowCount = table.getHeaderGroups().length;
  const pagedRowCount = manualPagination ? (rowCount ?? data.length) : table.getPrePaginationRowModel().rows.length;
  const isPartialPage = isGrid && !enableExpanding && pagedRowCount > table.getRowModel().rows.length;
  const ariaRowOffset = isPartialPage
    ? headerRowCount + effectivePagination.pageIndex * effectivePagination.pageSize
    : undefined;

  // Hover is stamped on the DOM so it never re-renders the table, and it follows keyboard focus as well as the pointer.
  const hoveredRowIdRef = useRef<string | null>(null);
  const hoveredRowElementsRef = useRef<HTMLElement[]>([]);

  const stampHoveredRow = useCallback((rowId: string | null) => {
    for (const el of hoveredRowElementsRef.current) el.removeAttribute('data-hovered');
    const container = sectionsContainerRef.current;
    const matches =
      rowId === null || !container
        ? []
        : Array.from(container.querySelectorAll<HTMLElement>('[data-slot="grid-row"][data-row-id]')).filter(
            (el) => el.dataset.rowId === rowId && el.closest('[data-slot="table-sections"]') === container,
          );
    for (const el of matches) el.setAttribute('data-hovered', 'true');
    hoveredRowElementsRef.current = matches;
  }, []);

  const setHoveredRow = useCallback(
    (rowId: string | null) => {
      if (rowId === hoveredRowIdRef.current) return;
      hoveredRowIdRef.current = rowId;
      stampHoveredRow(rowId);
    },
    [stampHoveredRow],
  );

  useLayoutEffect(() => {
    if (hoveredRowIdRef.current === null) return;
    const stamped = hoveredRowElementsRef.current;
    const intact =
      stamped.length > 0 && stamped.every((el) => el.isConnected && el.getAttribute('data-hovered') === 'true');
    if (!intact) stampHoveredRow(hoveredRowIdRef.current);
  });

  const tableBodyProps = {
    table,
    columnGroups,
    activeId,
    pinnedLayouts,
    isLoading,
    isFetching,
    highlightedRowIds,
    expandColumnId,
    stickyExpandedRows,
    spacing,
    fixedPinning,
    onRowHoverChange: setHoveredRow,
    enableRowHoverHighlight,
    getIsRowSelected:
      manualPagination && serverSideSelection ? (id: string) => serverSideSelection.getIsRowSelected(id) : undefined,
    onRowClick: handleRowClick,
    isGrid,
    ariaRowOffset,
    isSelectable: enableRowSelection,
  };

  return (
    <DndContext
      collisionDetection={closestLeftEdgeCollisionDetectionWithHideColumnDropzone}
      autoScroll={true}
      modifiers={[snapCenterToCursor]}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEndWithCleanup}
      sensors={sensors}
    >
      <div
        className={cn(
          'border-grey-30 flex h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border',
          enableHiding && overId === 'hide-column-dropzone' ? 'bg-red-10 border border-red-50' : '',
          className,
        )}
      >
        <HideColumnDropzone />
        {isGrid && (
          <span id={gridInstructionsId} className="sr-only">
            Use the arrow keys to move between cells. Press Enter or F2 to use the controls in a cell, and Escape to go
            back to the cell.
          </span>
        )}
        <output aria-live="polite" className="sr-only" data-testid="table-column-status">
          {columnStatus}
        </output>
        {(showPagination || showSearch) && (
          <div
            className={cn(
              'border-grey-30 flex items-center border-b px-2 py-1.5',
              showSearch ? 'justify-between' : 'justify-end',
            )}
          >
            {showSearch && (
              <SearchButtonInput
                value={effectiveGlobalFilter}
                onChange={handleGlobalFilterChange}
                className="h-6"
                data-testid="table-search"
              />
            )}
            {showPagination && (
              <TablePagination
                currentPage={effectivePagination.pageIndex + 1}
                totalItems={manualPagination ? (rowCount ?? data.length) : table.getFilteredRowModel().rows.length}
                pageSize={effectivePagination.pageSize}
                onPageChange={(page) => effectiveSetPagination({ ...effectivePagination, pageIndex: page - 1 })}
                isLoading={isLoading || isFetching}
                data-testid="table-pagination"
              />
            )}
          </div>
        )}
        {/* One scroll container so pinned sections move in the same frame as the center;
            sticky holds them in place. */}
        <div
          ref={sectionsContainerRef}
          data-slot="table-sections"
          className={cn(
            'min-h-0 flex-1 overflow-auto',
            '[&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar]:w-2.5',
            '[&::-webkit-scrollbar-thumb]:bg-grey-30 [&::-webkit-scrollbar-thumb:hover]:bg-grey-50 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-white',
            '[&::-webkit-scrollbar-track]:border [&::-webkit-scrollbar-track]:border-white [&::-webkit-scrollbar-track]:bg-transparent',
          )}
          style={
            {
              '--sticky-header-h': stickyExpandedRows ? `${stickyHeaderHeight}px` : undefined,
              maxHeight: maxHeight !== undefined ? `${maxHeight}px` : undefined,
            } as CSSProperties
          }
        >
          {/* With an expand column the row fits the container down to its content minimum, then scrolls. */}
          <Grid
            ref={gridRef}
            role={isGrid ? 'grid' : undefined}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-multiselectable={isGrid && enableRowSelection ? true : undefined}
            aria-rowcount={isPartialPage ? headerRowCount + pagedRowCount : undefined}
            spacing={spacing}
            style={columnSizeVars}
            noScrollContainer
            className={expandColumnId ? 'w-full min-w-min' : 'w-max min-w-full'}
            onMouseLeave={() => setHoveredRow(null)}
            onKeyDown={gridNavigation.onKeyDown}
            onFocus={gridNavigation.onFocus}
            onBlur={(e) => {
              gridNavigation.onBlur(e);
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHoveredRow(null);
            }}
          >
            <TableHeader
              table={table}
              columnGroups={columnGroups}
              activeId={activeId}
              overId={overId}
              pinnedLayouts={pinnedLayouts}
              stateControl={stateControl}
              isLoading={isLoading}
              fixedPinning={fixedPinning}
              expandColumnId={expandColumnId}
              isGrid={isGrid}
              indexRows={isPartialPage}
              onMoveColumn={moveColumn}
              onResizeColumn={resizeColumn}
            />
            {isResizingColumn || isDraggingColumn ? (
              <MemoizedTableBody {...tableBodyProps} />
            ) : (
              <TableBody {...tableBodyProps} />
            )}
          </Grid>
        </div>
      </div>
      <DragOverlay dropAnimation={null}>
        {activeHeader && activeHeader.column.id !== 'select' ? (
          <GridHeadDragOverlay
            label={
              typeof activeHeader.column.columnDef.header === 'string'
                ? activeHeader.column.columnDef.header
                : undefined
            }
            style={{ transform: '', width: `${activeHeader.getSize()}px` }}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export { Table };
